/**
 * Teach react-native-calendars Spanish.
 *
 * The library ships English month and day names and nothing configured it, so
 * every calendar in the app rendered "August 2026 / Mon Tue Wed Thu Fri Sat
 * Sun" surrounded by "Seleccioná tus fechas" and "Fecha de recogida". It went
 * out that way in the booking screenshot on the live Play Store listing.
 *
 * `LocaleConfig` in react-native-calendars is literally a re-export of XDate,
 * and the calendar header formats its month and weekday strings through
 * `XDate.locales[XDate.defaultLocale]`. So this configures XDate directly —
 * the barrel that exports LocaleConfig also drags in Agenda, ExpandableCalendar
 * and Timeline (moment, lodash, recyclerlistview: ~190 KB the app never
 * renders), which is the same reason DatePicker.tsx deep-imports the calendar.
 *
 * xdate ships no typings; the surface used here is declared in
 * `types/xdate.d.ts` rather than cast through `unknown`.
 *
 * Day and month names are lowercase because that is Spanish orthography; the
 * calendar header capitalises the first letter itself.
 */
import XDateDefault from 'xdate'

const LocaleConfig = XDateDefault

LocaleConfig.locales.en = {
  monthNames: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  monthNamesShort: [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ],
  dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  today: 'Today',
}

LocaleConfig.locales.es = {
  monthNames: [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ],
  monthNamesShort: [
    'ene',
    'feb',
    'mar',
    'abr',
    'may',
    'jun',
    'jul',
    'ago',
    'sep',
    'oct',
    'nov',
    'dic',
  ],
  dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
  dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
  today: 'hoy',
}

// Spanish is the primary language, so it is also the fallback: an unconfigured
// calendar showing Spanish is right far more often than one showing English.
LocaleConfig.defaultLocale = 'es'

/** Point the calendar at the app's current language. */
export function setCalendarLocale(language: string | undefined): void {
  LocaleConfig.defaultLocale = language?.startsWith('en') ? 'en' : 'es'
}
